// components/RemarkDialog.tsx
import { AxiosResponse } from 'axios';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { saveCopy } from 'src/app/services/ClientService';

interface DialogProps {
  visible: boolean;
  onClose: () => void;
  header: string;
  redemptionId?: string;
}

const SaveCopyDialog: React.FC<DialogProps> = ({
  visible,
  onClose,
  header,
  redemptionId
}) => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const [email, setEmail] = useState(''); //559900 for sample
  const [emailStatus, setEmailStatus] = useState('fail'); //559900 for sample
  const { t } = useTranslation();

  const handleHide = () => {
    setEmailStatus('fail');
    setEmail('');
    onClose();
  };

  const handleEmail = async () => {
    if (!email) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Enter email.',
        life: 3000,
      });
      return;
    }

    try {
      const response = await saveCopy(email, redemptionId) as AxiosResponse;
      console.log('Response ', response);

      if (response.status === 200) {
        setEmailStatus('success');
      }

    } catch (error: any) {
      console.log('Error ', error);
      if (error.response) {
        const { status, data } = error.response;
        if (status === 422) {
          toast.current?.show({
            severity: 'error',
            summary: 'Validation Error',
            detail: data.message || 'Invalid input provided.',
            life: 3000,
          });
        } else if (status === 500) {
          toast.current?.show({
            severity: 'error',
            summary: 'Server Error',
            detail:
              'An unexpected server error occurred. Please try again later.',
            life: 3000,
          });
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: data?.message || 'Something went wrong.',
            life: 3000,
          });
        }
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Network Error',
          detail:
            'Failed to submit data. Please check your connection and try again.',
          life: 3000,
        });
      }
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        style={{ width: '35vw' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        visible={visible}
        onHide={handleHide}
        dismissableMask
        closable={true}
      >
        <div className="w-full py-2 ">
          <div className="grid">
            <div className="col-12 lg:col-12 px-2">
              {emailStatus === 'fail' ? (
                <>
                  <h3 className="mb-3 text-center font-bold">{t('redeem_offer.save_copy_dialog.title')}</h3>

                  <p className="mb-5 text-center">
                    {t('redeem_offer.save_copy_dialog.description')}
                  </p>

                  <div className="flex flex-column gap-2 mb-4">
                    <span className="p-input-icon-left p-input-icon-right w-full">
                      <i className="pi pi-envelope"></i>
                      <InputText
                        id="email"
                        value={email}
                        placeholder={t('redeem_offer.save_copy_dialog.email_placeholder')}
                        className="w-full"
                        onChange={(e) => {
                          setEmail(e.target.value);
                        }}
                      />
                    </span>
                  </div>

                  <div className="flex flex-row gap-2">
                    <Button
                      className="w-full"
                      label={t('redeem_offer.save_copy_dialog.confirm')}
                      onClick={() => handleEmail()}
                    />
                  </div>
                </>
              ) : null}

              {emailStatus === 'success' ? (
                <>
                  <div className="text-center">
                    <i
                      className="pi  pi-check-circle text-center"
                      style={{ color: '#22C55E', fontSize: '2.5rem' }}
                    ></i>
                  </div>
                  <h3 className="mb-3 text-center font-bold">{t('redeem_offer.save_copy_dialog.sent')}</h3>

                  <p className="mb-5 text-center">
                    {t('redeem_offer.save_copy_dialog.sent_description')}
                  </p>

                  <div className="flex flex-row gap-2">
                    <Button
                      className="w-full"
                      label={t('redeem_offer.save_copy_dialog.got_it')}
                      onClick={() => handleHide()}
                    />
                  </div>

                  <p className="mt-2 mb-1 text-center">
                    {t('redeem_offer.save_copy_dialog.not_received')}
                  </p>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default SaveCopyDialog;
